import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import InviteModal from '../components/InviteModal';
import { useAuth } from '../contexts/AuthContext';
import UserAvatar from '../components/common/UserAvatar';
import { API_BASE_URL } from '../lib/apiConfig';
import { apiFetch } from '../lib/apiFetch';

// Types
interface TeamMember {
  id: number;
  name: string;
  role: string;
  email?: string;
  phone?: string;
  tipo_usuario: 'ADMIN' | 'ASSESSOR';
  nivel_acesso: string;
  created_at: string;
  foto_url?: string | null;
}

interface Invite {
  id: number;
  codigo: string;
  nome_assessor: string;
  nivel_assessor: 'BRONZE' | 'PRATA' | 'OURO';
  valido_ate: string;
  usado: boolean;
  criado_em: string;
  criador_nome?: string;
}

// Styled Components
const TeamContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #ffffff;
  padding: 40px;
  padding-bottom: 120px;
  overflow-x: hidden;
  max-width: 100vw;
  box-sizing: border-box;
`;

const Header = styled.div`
  margin-bottom: 40px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
  flex-wrap: wrap;
`;

const HeaderContent = styled.div`
  flex: 1;
  min-width: 250px;
`;

const AddButton = styled.button`
  padding: 12px 24px;
  background: var(--accent);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;

  &:hover {
    background: var(--accent-dark);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(var(--accent-rgb), 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const InvitesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
`;

const InvitesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const InviteCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const InviteHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 12px;
`;

const InviteInfo = styled.div`
  flex: 1;
`;

const InviteName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
`;

const InviteLevel = styled.div<{ level: string }>`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    if (props.level === 'OURO') return '#fef3c7';
    if (props.level === 'PRATA') return '#e5e7eb';
    return '#fed7aa';
  }};
  color: ${props => {
    if (props.level === 'OURO') return '#92400e';
    if (props.level === 'PRATA') return '#374151';
    return '#9a3412';
  }};
`;

const InviteCode = styled.div`
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 12px;
  font-family: 'Monaco', 'Courier New', monospace;
  font-size: 14px;
  font-weight: 600;
  color: var(--accent);
  text-align: center;
  margin-bottom: 8px;
  letter-spacing: 2px;
`;

const InviteExpiry = styled.div`
  font-size: 13px;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    width: 14px;
    height: 14px;
  }
`;

const LoadingSpinner = styled.div`
  width: 24px;
  height: 24px;
  border: 3px solid #e2e8f0;
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 20px auto;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const Title = styled.h1`
  font-size: 42px;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
  margin-bottom: 8px;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: #64748b;
  margin: 0;
`;

const SearchBar = styled.input`
  width: 100%;
  max-width: 500px;
  padding: 14px 20px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 50px;
  color: #1e293b;
  font-size: 15px;
  font-weight: 500;
  transition: all 0.3s ease;

  &::placeholder {
    color: #94a3b8;
  }

  &:focus {
    outline: none;
    background: #ffffff;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(var(--accent-rgb), 0.1);
  }
`;

const SearchAndViewContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const ViewModeButtons = styled.div`
  display: flex;
  gap: 8px;
  background: #f1f5f9;
  padding: 4px;
  border-radius: 12px;
`;

const ViewModeButton = styled.button<{ active: boolean }>`
  padding: 8px 12px;
  background: ${props => props.active ? '#ffffff' : 'transparent'};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${props => props.active ? 'var(--accent)' : '#64748b'};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${props => props.active ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none'};

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    color: var(--accent);
  }
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 32px;
  border-bottom: 2px solid #e2e8f0;
  padding-bottom: 0;
`;

const Tab = styled.button<{ active: boolean }>`
  padding: 12px 24px;
  background: none;
  border: none;
  font-size: 15px;
  font-weight: 600;
  color: ${props => props.active ? 'var(--accent)' : '#64748b'};
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
  border-bottom: 3px solid ${props => props.active ? 'var(--accent)' : 'transparent'};
  margin-bottom: -2px;

  &:hover {
    color: ${props => props.active ? 'var(--accent)' : '#1e293b'};
  }

  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    width: 18px;
    height: 18px;
  }
`;

const TabContent = styled.div`
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;
`;

const TeamList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const MemberListItem = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px;
  transition: all 0.3s ease;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 20px;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border-color: var(--accent);
  }
`;

const ListPhotoWrapper = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid #e2e8f0;
  flex-shrink: 0;
  transition: all 0.3s ease;

  ${MemberListItem}:hover & {
    border-color: var(--accent);
  }
`;

const ListMemberInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ListMemberHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const ListMemberName = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
`;

const ListMemberRole = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: var(--accent);
  background: rgba(var(--accent-rgb), 0.08);
  padding: 4px 12px;
  border-radius: 6px;
`;

const ListMemberCity = styled.div`
  font-size: 14px;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 6px;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const MemberCard = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 24px;
  transition: all 0.3s ease;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  &:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    transform: translateY(-4px);
    border-color: var(--accent);
  }
`;

const PhotoWrapper = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  overflow: hidden;
  margin-bottom: 16px;
  border: 3px solid #e2e8f0;
  transition: all 0.3s ease;

  ${MemberCard}:hover & {
    border-color: var(--accent);
  }
`;

const Photo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const MemberName = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px 0;
`;

const MemberRole = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--accent);
  margin-bottom: 12px;
`;

const MemberCity = styled.div`
  font-size: 14px;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 6px;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const EmptyState = styled.div`
  grid-column: 1 / -1;
  padding: 80px 24px;
  text-align: center;
  color: #64748b;
`;

const EmptyStateText = styled.div`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #475569;
`;

const EmptyStateSubtext = styled.div`
  font-size: 14px;
  color: #94a3b8;
`;

const Team: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin, session } = useAuth();
  const [activeTab, setActiveTab] = useState<'team' | 'invites'>('team');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loadingInvites, setLoadingInvites] = useState(false);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loadingTeam, setLoadingTeam] = useState(false);

  // Fetch team members from backend
  const fetchTeamMembers = async () => {
    if (!session?.access_token) return;
    
    setLoadingTeam(true);
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v1/usuarios/grupo`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          // Mapear dados do backend para interface TeamMember
          const mappedMembers: TeamMember[] = result.data.map((user: any) => ({
            id: user.id,
            name: user.nome,
            email: user.email_contato,
            tipo_usuario: user.tipo_usuario,
            nivel_acesso: user.nivel_acesso,
            role: user.tipo_usuario === 'ADMIN' ? 'Administrador' : `Assessor - ${user.nivel_acesso}`,
            created_at: user.created_at,
            foto_url: user.foto_url || null,
          }));
          setTeamMembers(mappedMembers);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar membros do time:', error);
    } finally {
      setLoadingTeam(false);
    }
  };

  // Fetch invites from backend
  const fetchInvites = async () => {
    if (!session?.access_token) return;
    
    setLoadingInvites(true);
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v1/convites`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setInvites(result.data);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar convites:', error);
    } finally {
      setLoadingInvites(false);
    }
  };

  // Load team members and invites on mount
  useEffect(() => {
    if (session) {
      fetchTeamMembers();
      if (isAdmin) {
        fetchInvites();
      }
    }
  }, [session, isAdmin]);

  // Check if there's a member name in the location state (from navigation)
  useEffect(() => {
    const state = location.state as { memberName?: string } | null;
    if (state?.memberName) {
      // Find the member by name
      const member = teamMembers.find(m => m.name === state.memberName);
      if (member) {
        handleMemberClick(member);
      }
      // Clear the state so it doesn't reopen on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate, teamMembers]);

  const filteredTeam = teamMembers.filter(member => 
    member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (member.email && member.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleMemberClick = (member: TeamMember) => {
    // TODO: Implementar detalhes do membro
    console.log('Membro selecionado:', member);
  };

  const handleInviteSuccess = () => {
    fetchInvites(); // Reload invites after creating new one
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const getPhotoUrl = (id: number) => {
    // Gerar URL de foto baseado no ID para consistência
    const avatarId = (id % 70) + 1; // pravatar tem ~70 imagens
    return `https://i.pravatar.cc/300?img=${avatarId}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const isExpired = (dateString: string) => {
    return new Date(dateString) < new Date();
  };

  const pendingInvites = invites.filter(inv => !inv.usado && !isExpired(inv.valido_ate));

  return (
    <>
      <TeamContainer>
      <Header>
        <HeaderContent>
          <Title>Time</Title>
          <Subtitle>
            Conheça os membros da equipe da campanha
          </Subtitle>
        </HeaderContent>
        {isAdmin && (
          <AddButton onClick={() => setIsInviteModalOpen(true)}>
            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Adicionar Usuário
          </AddButton>
        )}
      </Header>

      <SearchAndViewContainer>
        <SearchBar
          type="text"
          placeholder={activeTab === 'team' ? "Buscar por nome, cidade ou função..." : "Buscar convites..."}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <ViewModeButtons>
          <ViewModeButton 
            active={viewMode === 'grid'} 
            onClick={() => setViewMode('grid')}
            title="Visualização em Grade"
          >
            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
          </ViewModeButton>
          <ViewModeButton 
            active={viewMode === 'list'} 
            onClick={() => setViewMode('list')}
            title="Visualização em Lista"
          >
            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </ViewModeButton>
        </ViewModeButtons>
      </SearchAndViewContainer>

      <TabsContainer>
        <Tab active={activeTab === 'team'} onClick={() => setActiveTab('team')}>
          <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          Usuários Criados
        </Tab>
        {isAdmin && (
          <Tab active={activeTab === 'invites'} onClick={() => setActiveTab('invites')}>
            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Convites Enviados
          </Tab>
        )}
      </TabsContainer>

      <TabContent>
        {activeTab === 'team' ? (
          loadingTeam ? (
            <LoadingSpinner />
          ) : viewMode === 'grid' ? (
            <TeamGrid>
              {filteredTeam.length === 0 ? (
                <EmptyState>
                  <EmptyStateText>Nenhum membro encontrado</EmptyStateText>
                  <EmptyStateSubtext>
                    {teamMembers.length === 0 ? 'Nenhum membro cadastrado ainda' : 'Tente ajustar sua busca'}
                  </EmptyStateSubtext>
                </EmptyState>
              ) : (
                filteredTeam.map((member) => (
                  <MemberCard key={member.id} onClick={() => handleMemberClick(member)}>
                    <PhotoWrapper>
                      <UserAvatar name={member.name} fotoUrl={member.foto_url} size={120} />
                    </PhotoWrapper>
                    <MemberName>{member.name}</MemberName>
                    <MemberRole>{member.role}</MemberRole>
                    <MemberCity>
                      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      {member.email || 'Sem e-mail'}
                    </MemberCity>
                  </MemberCard>
                ))
              )}
            </TeamGrid>
          ) : (
            <TeamList>
              {filteredTeam.length === 0 ? (
                <EmptyState>
                  <EmptyStateText>Nenhum membro encontrado</EmptyStateText>
                  <EmptyStateSubtext>
                    {teamMembers.length === 0 ? 'Nenhum membro cadastrado ainda' : 'Tente ajustar sua busca'}
                  </EmptyStateSubtext>
                </EmptyState>
              ) : (
                filteredTeam.map((member) => (
                  <MemberListItem key={member.id} onClick={() => handleMemberClick(member)}>
                    <ListPhotoWrapper>
                      <UserAvatar name={member.name} fotoUrl={member.foto_url} size={80} />
                    </ListPhotoWrapper>
                    <ListMemberInfo>
                      <ListMemberHeader>
                        <ListMemberName>{member.name}</ListMemberName>
                        <ListMemberRole>{member.role}</ListMemberRole>
                      </ListMemberHeader>
                      <ListMemberCity>
                        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        {member.email || 'Sem e-mail'}
                      </ListMemberCity>
                    </ListMemberInfo>
                  </MemberListItem>
                ))
              )}
            </TeamList>
          )
        ) : (
          <>
            {loadingInvites ? (
              <LoadingSpinner />
            ) : pendingInvites.length === 0 ? (
              <EmptyState>
                <EmptyStateText>Nenhum convite pendente</EmptyStateText>
                <EmptyStateSubtext>
                  Clique em "Adicionar Usuário" para criar um novo convite
                </EmptyStateSubtext>
              </EmptyState>
            ) : viewMode === 'grid' ? (
              <InvitesGrid>
                {pendingInvites.map((invite) => (
                  <InviteCard key={invite.id}>
                    <InviteHeader>
                      <InviteInfo>
                        <InviteName>{invite.nome_assessor}</InviteName>
                      </InviteInfo>
                    </InviteHeader>
                    <InviteCode>{invite.codigo}</InviteCode>
                    <InviteExpiry>
                      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Válido até {formatDate(invite.valido_ate)}
                    </InviteExpiry>
                  </InviteCard>
                ))}
              </InvitesGrid>
            ) : (
              <InvitesList>
                {pendingInvites.map((invite) => (
                  <InviteCard key={invite.id}>
                    <InviteHeader>
                      <InviteInfo>
                        <InviteName>{invite.nome_assessor}</InviteName>
                      </InviteInfo>
                    </InviteHeader>
                    <InviteCode>{invite.codigo}</InviteCode>
                    <InviteExpiry>
                      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Válido até {formatDate(invite.valido_ate)}
                    </InviteExpiry>
                  </InviteCard>
                ))}
              </InvitesList>
            )}
          </>
        )}
      </TabContent>
    </TeamContainer>

    <InviteModal
      isOpen={isInviteModalOpen}
      onClose={() => setIsInviteModalOpen(false)}
      onSuccess={handleInviteSuccess}
    />
    </>
  );
};

export default Team;
